import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { EventService } from '../../services/event.service';
import { CurrentEventService } from '../../services/current-event.service';
import { GenericEvent } from '../../models/event.model';
import { Subject, takeUntil, finalize } from 'rxjs';

@Component({
  selector: 'app-thank-you',
  imports: [CommonModule],
  templateUrl: './thank-you.html',
  styleUrl: './thank-you.css',
})
export class ThankYouComponent implements OnInit, OnDestroy {
  event: GenericEvent | null = null;
  loading = false;
  error: string | null = null;

  private destroy$ = new Subject<void>();

  constructor(
    private eventService: EventService,
    private currentEventService: CurrentEventService,
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    // Try to get event from CurrentEventService first (already decoded)
    const cached = this.currentEventService.event;
    if (cached) {
      this.event = cached;
      return;
    }

    // Fall back to re-decoding the token
    this.route.queryParams.pipe(
      takeUntil(this.destroy$)
    ).subscribe(params => {
      const token = params['token'];

      if (!token) {
        this.error = 'No event specified. A registration token is required.';
        return;
      }

      this.loading = true;
      this.error = null;

      this.eventService.decodeToken(token).pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.loading = false;
          this.cdr.detectChanges();
        })
      ).subscribe({
        next: (event) => {
          this.event = event;
          this.currentEventService.set(event);
        },
        error: () => {
          this.error = 'An error occurred while loading the event.';
        }
      });
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  formatTimezone(): string {
    if (!this.event?.timezone) return '';
    try {
      const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: this.event.timezone,
        timeZoneName: 'short',
      }).formatToParts(new Date());
      return parts.find(p => p.type === 'timeZoneName')?.value || this.event.timezone;
    } catch {
      return this.event.timezone;
    }
  }
}
